import moment from 'moment';
import { ALIGNMENT_FILTER_STATUS } from '../../../../constants/okr/alignmentFilter.constants';

type TFn = (key: string) => string;

export type AlignmentFilterValue = {
  label?: string;
  value?: string | number;
  startDate?: string;
  endDate?: string;
  periodName?: string;
  fiscalYear?: number;
};

export type AlignmentFilterCriterion = {
  fieldName: string;
  fieldType: string;
  operator: string;
  values: AlignmentFilterValue[];
  nonRemovable?: boolean;
};

export type AlignmentNode = {
  id?: string | number;
  status?: string | number;
  start_date?: string | number;
  target_date?: string | number;
  team_id?: string | number;
  teamName?: string;
  goals?: AlignmentNode[];
};

export type TeamOption = {
  label: string;
  value: string;
};

export const TIME_PERIOD_OPERATOR = {
  FALLS_WITHIN: 'falls within',
  EXACTLY_EQUALS: 'exactly equals',
};

export const getDefaultCriteria = (t: TFn): AlignmentFilterCriterion[] => [
  {
    fieldName: 'status',
    fieldType: 'multi-select-button',
    operator: 'is',
    values: [{ label: t('Open'), value: ALIGNMENT_FILTER_STATUS.OPEN }],
    nonRemovable: true,
  },
  {
    fieldName: 'timePeriod',
    fieldType: 'time-periods-select',
    operator: TIME_PERIOD_OPERATOR.FALLS_WITHIN,
    values: [],
    nonRemovable: true,
  },
  {
    fieldName: 'team',
    fieldType: 'multi-select',
    operator: 'is',
    values: [],
    nonRemovable: true,
  },
];

export const getTeamOptions = (nodes: AlignmentNode[] = []): TeamOption[] => {
  const byId = new Map<string, TeamOption>();
  const walk = (list: AlignmentNode[]) => list.forEach((node) => {
    if (node.team_id !== undefined && node.team_id !== null && node.teamName) {
      byId.set(String(node.team_id), { label: node.teamName, value: String(node.team_id) });
    }
    if (Array.isArray(node.goals)) {
      walk(node.goals);
    }
  });
  walk(nodes);
  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
};

export const getActiveFilterCount = (criteria: AlignmentFilterCriterion[] = []): number =>
  criteria.filter((c) => Array.isArray(c.values) && c.values.length > 0).length;

export const toUtcMidnightUnix = (date: string | number): number | null => {
  if (date === null || date === undefined || date === '') {
    return null;
  }
  const calendarDay = moment(date).format('YYYY-MM-DD');
  const parsed = moment.utc(calendarDay, 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed.unix() : null;
};

const toUnixSeconds = (value: string | number | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const matchesStatus = (
  node: AlignmentNode,
  criterion?: AlignmentFilterCriterion,
): boolean => {
  const values = criterion?.values ?? [];
  if (!values.length) {
    return true;
  }
  const selected = values.map((v) => String(v.value));
  return selected.includes(String(node.status));
};

export const matchesTimePeriod = (
  node: AlignmentNode,
  criterion?: AlignmentFilterCriterion,
): boolean => {
  const values = criterion?.values ?? [];
  if (!values.length) {
    return true;
  }
  const nodeStart = toUnixSeconds(node.start_date);
  const nodeTarget = toUnixSeconds(node.target_date);
  const exact = criterion?.operator === TIME_PERIOD_OPERATOR.EXACTLY_EQUALS;

  return values.some((value) => {
    const periodStart = toUtcMidnightUnix(value?.startDate);
    const periodEnd = toUtcMidnightUnix(value?.endDate);
    if (periodStart === null || periodEnd === null || nodeStart === null || nodeTarget === null) {
      return false;
    }
    if (exact) {
      return nodeStart === periodStart && nodeTarget === periodEnd;
    }
    return nodeStart >= periodStart && nodeTarget <= periodEnd;
  });
};

// time-periods-select leaves a removed period in the form value on last-chip
// removal, so drop chip-field values whose chip is no longer shown in the panel.
export const reconcileChipCriteria = (
  criteria: AlignmentFilterCriterion[] = [],
  shownLabels: Set<string>,
): AlignmentFilterCriterion[] =>
  criteria.map((c) => {
    if (c.fieldType !== 'time-periods-select' && c.fieldType !== 'multi-select') {
      return c;
    }
    const values = (c.values ?? []).filter((v) => v?.label != null && shownLabels.has(v.label));
    return { ...c, values };
  });

export const matchesTeam = (
  node: AlignmentNode,
  criterion?: AlignmentFilterCriterion,
): boolean => {
  const values = criterion?.values ?? [];
  if (!values.length) {
    return true;
  }
  const selected = values.map((v) => String(v.value));
  return selected.includes(String(node.team_id));
};

export const reconcileTeamCriteria = (
  criteria: AlignmentFilterCriterion[] = [],
  teamOptions: TeamOption[] = [],
): AlignmentFilterCriterion[] => {
  const validIds = new Set(teamOptions.map((o) => o.value));
  return criteria.map((c) => (c.fieldName === 'team'
    ? { ...c, values: (c.values ?? []).filter((v) => validIds.has(String(v.value))) }
    : c));
};

export const nodeMatchesFilters = (
  node: AlignmentNode,
  criteria: AlignmentFilterCriterion[] = [],
): boolean => {
  const status = criteria.find((c) => c.fieldName === 'status');
  const timePeriod = criteria.find((c) => c.fieldName === 'timePeriod');
  const team = criteria.find((c) => c.fieldName === 'team');
  return matchesStatus(node, status)
    && matchesTimePeriod(node, timePeriod)
    && matchesTeam(node, team);
};

const isPinned = (node: AlignmentNode, pinnedId?: string | number): boolean =>
  pinnedId !== undefined && pinnedId !== null && String(node.id) === String(pinnedId);

// Single pass marking the pinned node and every ancestor of it, so the prune
// step can decide each node with an O(1) lookup instead of re-walking subtrees.
const collectPinnedKeep = (
  nodes: AlignmentNode[],
  pinnedId: string | number | undefined,
  keep: Set<AlignmentNode>,
): boolean => {
  let subtreePinned = false;
  nodes.forEach((node) => {
    const childHasPinned = Array.isArray(node.goals) && node.goals.length
      ? collectPinnedKeep(node.goals, pinnedId, keep)
      : false;
    if (isPinned(node, pinnedId) || childHasPinned) {
      keep.add(node);
      subtreePinned = true;
    }
  });
  return subtreePinned;
};

const pruneTree = (
  nodes: AlignmentNode[],
  criteria: AlignmentFilterCriterion[],
  keep: Set<AlignmentNode>,
): AlignmentNode[] =>
  nodes
    .filter((node) => keep.has(node) || nodeMatchesFilters(node, criteria))
    .map((node) => (Array.isArray(node.goals) && node.goals.length
      ? { ...node, goals: pruneTree(node.goals, criteria, keep) }
      : node));

// Drops a non-matching node with its whole subtree; the pinned node and its ancestors always stay.
export const filterAlignmentTree = (
  nodes: AlignmentNode[] = [],
  criteria: AlignmentFilterCriterion[] = [],
  pinnedId?: string | number,
): AlignmentNode[] => {
  const keep = new Set<AlignmentNode>();
  collectPinnedKeep(nodes, pinnedId, keep);
  return pruneTree(nodes, criteria, keep);
};
